import type { OrderDetails as GQLItem } from '../../../generated/api.graphql';
import { Account } from '../Account';
import { Event } from '../Event';
import { Customer } from '../Customer';
import { take } from '../../../util';
import type * as Order from './Order';
import * as LineItem from './LineItem';
import { RefundStatus } from './RefundStatus';
import * as Refund from './Refund';

/**
 * The schema's `OrderDetails` type — everything `Order` carries, plus the line items, fees, credit
 * and refund state this read adds.
 *
 * The intersection with `Order` is a type-level statement that this is the same order seen in more
 * detail. The **mapper** does not compose that way: it re-lists `Order`'s scalars in its own `take`
 * rather than spreading `Order.fromGql(item)`, because `OrderDetails` and `Order` are two distinct
 * wire types and a mapper takes one of them. `App/Event/EventDetail.ts` sets the same precedent — a
 * standalone mapper per read shape.
 *
 * The cost: adding a scalar to `Order` declares it here for free but maps nothing, and neither `take`
 * nor this module's fixture-bound spec will say so. Change both `take` lists together.
 */
export type OrderDetail = Order.Order & {
  lineItems: LineItem.LineItem[];
  purchasedAt?: string;
  appliedCredit?: number;
  donation: number;
  processingFee: number;
  taxFee: number;
  /** Order-level platform fee, matching the amount sent to Stripe. */
  platformFee: number;
  refundStatus: RefundStatus;
  /** Oldest first. */
  refunds: Refund.Refund[];
  /** Resolved FGL layout string for the order, used by desktop POS ticket printing. */
  ticketLayout?: string;
  /** The order's own contact, which `updateOrderCustomer` edits; `customer` is the global record. */
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
};

export const fromGql = (item: GQLItem): OrderDetail => ({
  ...take<OrderDetail>(item, 'id', 'createdAt', 'updatedAt', 'state', 'fingerprint', 'transactionId', 'total', 'currency', 'origin', 'scannedCount', 'ticketCount', 'purchasedAt', 'paymentMethod', 'cardBrand', 'lastFourDigits', 'appliedCredit', 'donation', 'processingFee', 'taxFee', 'platformFee', 'refundStatus', 'ticketLayout', 'firstName', 'lastName', 'email', 'phoneNumber'),
  account: item.account ? Account.fromGql(item.account) : undefined,
  event: item.event ? Event.fromGql(item.event) : undefined,
  customer: item.customer ? Customer.fromGql(item.customer) : undefined,
  lineItems: item.lineItems.map(LineItem.fromGql),
  refunds: item.refunds.map(Refund.fromGql),
});
